import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { MdChevronLeft, MdChevronRight, MdDirectionsCar, MdLocationCity } from 'react-icons/md';
import './App.css';
import { TechChipList, personalEnvItems, techLanguages, techTools } from './TechStack';

const CityIcon = MdLocationCity as React.ElementType;
const CarIcon = MdDirectionsCar as React.ElementType;
const ChevronLeftIcon = MdChevronLeft as React.ElementType;
const ChevronRightIcon = MdChevronRight as React.ElementType;

function App() {
  const projectsScrollRef = useRef<HTMLDivElement>(null);
  const [projectsOverflow, setProjectsOverflow] = useState(false);

  const updateProjectsOverflow = useCallback(() => {
    const el = projectsScrollRef.current;
    if (!el) return;
    setProjectsOverflow(el.scrollWidth > el.clientWidth + 2);
  }, []);

  useLayoutEffect(() => {
    updateProjectsOverflow();
    const el = projectsScrollRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => updateProjectsOverflow());
    ro.observe(el);
    window.addEventListener('resize', updateProjectsOverflow);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', updateProjectsOverflow);
    };
  }, [updateProjectsOverflow]);

  const scrollProjects = useCallback((direction: -1 | 1) => {
    const el = projectsScrollRef.current;
    if (!el) return;
    const gapStr = window.getComputedStyle(el).gap || '20px';
    const gap = parseFloat(gapStr) || 20;
    const first = el.querySelector<HTMLElement>('.project-card');
    const step = first ? first.offsetWidth + gap : 320;
    el.scrollBy({ left: direction * step, behavior: 'smooth' });
  }, []);

  return (
    <div className="App cv-page-root portfolio-page">
      <main className="cv-main-wrap cv-main-fullpage">
        <header className="cv-scroll-section cv-theme-band cv-theme-intro portfolio-hero">
          <div className="cv-band-inner cv-hero-inner">
            <div className="cv-header cv-header-hero">
              <div className="portfolio-hero-content">
                <div className="cv-header-main">
                  <p className="portfolio-eyebrow">Portfolio</p>
                  <h1 className="cv-name">Lucien Moreau</h1>
                  <nav className="cv-header-socials" aria-label="Profils en ligne">
                    <a
                      href="https://www.linkedin.com/in/lucien-moreau/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="cv-social cv-social-linkedin"
                      aria-label="LinkedIn"
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                      </svg>
                      LinkedIn
                    </a>
                    <a
                      href="https://github.com/Q7I"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="cv-social cv-social-github"
                      aria-label="GitHub"
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                        <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
                      </svg>
                      GitHub
                    </a>
                  </nav>
                </div>
                <ul className="cv-contact portfolio-hero-meta" aria-label="Coordonnées">
                  <li className="cv-contact-block">
                    <CityIcon className="cv-contact-block-icon" aria-hidden />
                    <div className="cv-contact-block-text">
                      <span className="cv-contact-label">Ville</span>
                      <span className="cv-contact-value">Bordeaux</span>
                    </div>
                  </li>
                  <li className="cv-contact-block">
                    <CarIcon className="cv-contact-block-icon" aria-hidden />
                    <div className="cv-contact-block-text">
                      <span className="cv-contact-label">Permis de conduire</span>
                      <span className="cv-contact-value">Permis B, véhiculé</span>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </header>

        <section
          className="cv-scroll-section cv-theme-band cv-theme-projects-band projects-showcase cv-projects-fullwidth"
          aria-labelledby="projets-recents-heading"
        >
          <div className="cv-band-inner">
            <h3 id="projets-recents-heading" className="pf-section-heading">
              Mes projets récents
            </h3>
          </div>
          <div className="projects-carousel-fullbleed">
            <div className="projects-carousel-shell">
              {projectsOverflow ? (
                <button
                  type="button"
                  className="projects-carousel-arrow projects-carousel-arrow--prev"
                  onClick={() => scrollProjects(-1)}
                  aria-label="Voir les projets précédents"
                >
                  <ChevronLeftIcon aria-hidden />
                </button>
              ) : null}
              <div
                className={`projects-carousel${projectsOverflow ? '' : ' projects-carousel--no-arrows'}`}
                ref={projectsScrollRef}
              >
                <div className="project-card">
                  <div className="project-image">
                    <img src="/paysagiste.JPEG" alt="Ritz Paysage" />
                  </div>
                  <div className="project-content">
                    <h4>Ritz Paysage</h4>
                    <p>
                      Site web pour une entreprise de paysagisme dans le Calvados, spécialisée dans
                      l&apos;élagage, le terrassement et l&apos;aménagement paysager.
                    </p>
                    <div className="project-highlights">
                      <span className="highlight">Design élégant</span>
                      <span className="highlight">SEO agressif</span>
                      <span className="highlight">Gestion de contenu</span>
                    </div>
                    <div className="project-tags">
                      <span className="tag">ReactJS</span>
                      <span className="tag">TypeScript</span>
                      <span className="tag">Material-UI</span>
                    </div>
                    <a
                      href="https://paysagiste-secteur-calvados.fr/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="project-link"
                    >
                      Voir le projet →
                    </a>
                  </div>
                </div>

                <div className="project-card">
                  <div className="project-image">
                    <img src="/renovation.JPEG" alt="Technique Rénovation 78" />
                  </div>
                  <div className="project-content">
                    <h4>Technique Rénovation 78</h4>
                    <p>
                      Site web professionnel pour une entreprise de rénovation dans les Yvelines,
                      spécialisée dans la rénovation complète de logements et locaux commerciaux.
                    </p>
                    <div className="project-highlights">
                      <span className="highlight">Design professionnel</span>
                      <span className="highlight">SEO optimisé</span>
                      <span className="highlight">Performance web</span>
                    </div>
                    <div className="project-tags">
                      <span className="tag">JavaScript</span>
                      <span className="tag">React</span>
                      <span className="tag">Node.js</span>
                      <span className="tag">Material-UI</span>
                    </div>
                    <a
                      href="https://technique-renovation78.fr/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="project-link"
                    >
                      Voir le projet →
                    </a>
                  </div>
                </div>
                <div className="projects-carousel-end-spacer" aria-hidden />
              </div>
              {projectsOverflow ? (
                <button
                  type="button"
                  className="projects-carousel-arrow projects-carousel-arrow--next"
                  onClick={() => scrollProjects(1)}
                  aria-label="Voir les projets suivants"
                >
                  <ChevronRightIcon aria-hidden />
                </button>
              ) : null}
            </div>
          </div>
        </section>

        <section className="cv-scroll-section cv-theme-band cv-theme-parcours-band">
          <div className="cv-band-inner cv-band-parcours">
            <div className="cv-columns-full">
              <aside className="cv-sidebar" aria-label="Compétences et informations">
                <section className="cv-side-section">
                  <h2 className="pf-section-heading">Compétences techniques</h2>
                  <h3 className="cv-side-sub">Langages &amp; scripts</h3>
                  <TechChipList items={techLanguages} />
                  <h3 className="cv-side-sub">Outils &amp; environnement</h3>
                  <TechChipList items={techTools} dense />
                </section>

                <section className="cv-side-section cv-side-section--extra-top cv-side-infra">
                  <h3 className="cv-side-sub">Environnement perso</h3>
                  <p className="cv-side-text cv-side-infra-lead">Expérience complémentaire :</p>
                  <TechChipList items={personalEnvItems} dense />
                </section>

                <section className="cv-side-section cv-side-section--extra-top">
                  <h3 className="cv-side-sub">Centres d&apos;intérêt</h3>
                  <p className="cv-side-text">
                    Sécurité incendie, marchés financiers et cryptomonnaies.
                  </p>
                </section>

                <section className="cv-side-section cv-side-section--extra-top">
                  <h3 className="cv-side-sub">Certifications</h3>
                  <ul className="cv-side-cert-list">
                    <li>SST &amp; SSIAP</li>
                  </ul>
                </section>
              </aside>

              <div className="cv-main">
                <section className="cv-section cv-section-exp">
                  <h2 className="pf-section-heading">Expérience professionnelle</h2>

                  <article className="cv-job cv-job--with-logo">
                    <div className="cv-job-block">
                      <img
                        className="cv-job-logo"
                        src="/logos/maif.svg"
                        alt=""
                        width={40}
                        height={40}
                      />
                      <div className="cv-job-block-text">
                        <div className="cv-job-title-row">
                          <h3 className="cv-job-title">Testeur logiciel indépendant</h3>
                          <time className="cv-job-dates" dateTime="2024-05-01">
                            avril 2024 — décembre 2025
                          </time>
                        </div>
                        <p className="cv-job-org">MAIF VIE — assureur spécialisé assurance vie</p>
                        <p className="cv-job-loc">200 av. Salvador Allende, 79000 Niort</p>
                        <ul className="cv-bullets">
                          <li>Exécution de cas de test et campagnes de non-régression</li>
                          <li>Mise à jour des flux et scénarios XML / SOAP (Postman)</li>
                          <li>Contribution à la documentation technique React (Docusaurus)</li>
                          <li>Création et suivi de tickets de bogue</li>
                        </ul>
                      </div>
                    </div>
                  </article>

                  <article className="cv-job cv-job--with-logo">
                    <div className="cv-job-block">
                      <img
                        className="cv-job-logo"
                        src="/logos/darva.jpg"
                        alt=""
                        width={40}
                        height={40}
                      />
                      <div className="cv-job-block-text">
                        <div className="cv-job-title-row">
                          <h3 className="cv-job-title">
                            Automaticien de test front / back-end · Testeur logiciel
                          </h3>
                          <time className="cv-job-dates" dateTime="2020-09">
                            sept. 2020 — août 2023
                          </time>
                        </div>
                        <p className="cv-job-org">
                          DARVA — développement d&apos;applications sur réseaux à valeur ajoutée
                        </p>
                        <p className="cv-job-loc">245 rue du Stade, 79180 Chauray</p>
                        <ul className="cv-bullets">
                          <li>Conception d&apos;un script Postman pour générer une liste d&apos;utilisateurs applicatifs</li>
                          <li>Migration des jeux de données importés depuis Gist vers GitHub dans Postman</li>
                          <li>Conception et maintenance de scénarios de tests automatisés (Playwright)</li>
                          <li>Mise à jour des scénarios de tests API (Postman)</li>
                          <li>Rédaction de cas de test et création de tickets de bogue</li>
                        </ul>
                      </div>
                    </div>
                  </article>

                  <article className="cv-job cv-job--with-logo">
                    <div className="cv-job-block">
                      <img
                        className="cv-job-logo"
                        src="/logos/darva.jpg"
                        alt=""
                        width={40}
                        height={40}
                      />
                      <div className="cv-job-block-text">
                        <div className="cv-job-title-row">
                          <h3 className="cv-job-title">Testeur logiciel</h3>
                          <time className="cv-job-dates" dateTime="2017-09">
                            sept. 2017 — juil. 2020
                          </time>
                        </div>
                        <p className="cv-job-org">
                          DARVA — développement d&apos;applications sur réseaux à valeur ajoutée
                        </p>
                        <p className="cv-job-loc">245 rue du Stade, 79180 Chauray</p>
                        <ul className="cv-bullets">
                          <li>Mise en place d&apos;outils de test automatique front (Cypress)</li>
                          <li>TNR et tests de non-régression sur l&apos;application</li>
                          <li>Rédaction de documentation technique</li>
                          <li>Création de tickets de bogue</li>
                        </ul>
                      </div>
                    </div>
                  </article>
                </section>
              </div>
            </div>
          </div>
        </section>

        <section className="cv-scroll-section cv-theme-band cv-theme-formation-band" aria-labelledby="formation-heading">
          <div className="cv-band-formation">
            <section className="cv-section cv-section-formation-only">
              <h2
                id="formation-heading"
                className="cv-section-title cv-section-title--with-suffix"
              >
                Formation
                <span className="cv-section-title-suffix">Diplôme d&apos;État</span>
              </h2>
              <ul className="cv-education">
                <li>
                  <div className="cv-edu-main">
                    <strong>SEO, marketing web et cybersécurité informatique — peut-être avec vous ?</strong>
                    <span>Formation en alternance — Bordeaux</span>
                  </div>
                  <span className="cv-edu-years">
                    Recherche d&apos;alternance / Projet intéressant
                  </span>
                </li>
                <li>
                  <div className="cv-edu-main">
                    <strong>BTS SIO option B — solutions logicielles et applications métiers (SLAM)</strong>
                    <span>Ensemble scolaire Fénelon Notre-Dame — La Rochelle</span>
                  </div>
                  <span className="cv-edu-years">2020 — 2023</span>
                </li>
                <li>
                  <div className="cv-edu-main">
                    <strong>
                      Bac professionnel SN option C — réseaux informatiques et systèmes communicants
                    </strong>
                    <span>Lycée professionnel Rompsay — La Rochelle</span>
                  </div>
                  <span className="cv-edu-years">2019 — 2020</span>
                </li>
                <li>
                  <div className="cv-edu-main">
                    <strong>BEP — spécialité systèmes numériques</strong>
                    <span>Lycée professionnel Rompsay — La Rochelle</span>
                  </div>
                  <span className="cv-edu-years">2017 — 2019</span>
                </li>
              </ul>
            </section>
          </div>
        </section>
      </main>
      <footer className="portfolio-footer" role="contentinfo">
        <p>
          © {new Date().getFullYear()} Lucien Moreau ·{' '}
          <a href="https://lucien.ovh" className="portfolio-footer-link">
            lucien.ovh
          </a>
        </p>
      </footer>
    </div>
  );
}

export default App;
